/* Copper Studio — "Build your set" collection section (sections/cs-bundle-builder.liquid). ES module. */
import { CartAddEvent } from '@theme/events';

class CsBundle extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-card]')];
    this.byId = new Map(this.cards.map((card) => [card.dataset.id, card]));
    this.set = new Map();
    this.order = [];
    this.filter = 'all';
    this.shown = Number(this.dataset.perPage) || 24;
    this.perPage = this.shown;
    this.tiers = (this.dataset.tiers || '')
      .split('|')
      .filter(Boolean)
      .map((tier) => {
        const [qty, label] = tier.split(':');
        return { qty: Number(qty), label, pct: parseFloat(label) || 0 };
      });
    this.money = new Intl.NumberFormat(this.dataset.locale || 'en-GB', {
      style: 'currency',
      currency: this.dataset.currency || 'GBP',
    });

    this.panel = this.querySelector('[data-panel]');
    this.modal = this.querySelector('[data-modal]');
    const maxTier = this.tiers.length ? this.tiers[this.tiers.length - 1].qty : 4;
    const prog = this.querySelector('[data-prog]');
    prog.style.setProperty('--csb-steps', String(maxTier));
    prog.replaceChildren(...Array.from({ length: maxTier }, () => document.createElement('i')));

    this.addEventListener('click', (event) => this.onClick(event));
    this.modal.addEventListener('click', (event) => {
      if (event.target === this.modal) this.closeModal();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.modal.hidden) this.closeModal();
    });
    this.applyFilter();
    this.render();
  }

  format(cents) {
    return this.money.format(cents / 100);
  }

  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const card = target.closest('[data-card]');
    const id = card?.dataset.id || target.closest('[data-line]')?.dataset.line;

    if (target.closest('[data-cat]')) return this.setFilter(target.closest('[data-cat]'));
    if (target.closest('[data-more]')) {
      this.shown += this.perPage;
      return this.applyFilter();
    }
    if (target.closest('[data-open]') && card) return this.openModal(card);
    if (target.closest('[data-close]')) return this.closeModal();
    if (target.closest('[data-gprev]')) return this.goToImage(this.galleryIndex() - 1);
    if (target.closest('[data-gnext]')) return this.goToImage(this.galleryIndex() + 1);
    if (target.closest('[data-gthumb]')) return this.goToImage(Number(target.closest('[data-gthumb]').dataset.gthumb));
    if (target.closest('[data-madd]') && this.modalId) {
      this.change(this.modalId, 1);
      return this.closeModal();
    }
    if (target.closest('[data-add], [data-plus]') && id) return this.change(id, 1);
    if (target.closest('[data-minus]') && id) return this.change(id, -1);
    if (target.closest('[data-remove]') && id) return this.change(id, -(this.set.get(id) || 0));
    if (target.closest('[data-cta]')) return this.addToCart();
  }

  setFilter(button) {
    this.filter = button.dataset.cat;
    this.shown = this.perPage;
    for (const cat of this.querySelectorAll('[data-cat]')) {
      const on = cat === button;
      cat.classList.toggle('is-on', on);
      cat.setAttribute('aria-pressed', String(on));
    }
    this.applyFilter();
  }

  applyFilter() {
    let visible = 0;
    let matching = 0;
    for (const card of this.cards) {
      const match = this.filter === 'all' || card.dataset.cats.split(' ').includes(this.filter);
      if (match) matching += 1;
      const show = match && visible < this.shown;
      if (show) visible += 1;
      card.hidden = !show;
    }
    this.querySelector('[data-count]').textContent = String(matching);
    this.querySelector('[data-more-wrap]').hidden = visible >= matching;
  }

  change(id, delta) {
    const qty = Math.max(0, (this.set.get(id) || 0) + delta);
    if (qty === 0) {
      this.set.delete(id);
      this.order = this.order.filter((item) => item !== id);
    } else {
      if (!this.set.has(id)) this.order.push(id);
      this.set.set(id, qty);
    }
    this.render();
  }

  render() {
    let pieces = 0;
    let total = 0;
    for (const [id, qty] of this.set) {
      pieces += qty;
      total += Number(this.byId.get(id).dataset.price) * qty;
    }

    for (const card of this.cards) {
      const qty = this.set.get(card.dataset.id) || 0;
      card.classList.toggle('is-sel', qty > 0);
      card.querySelector('[data-qb]').textContent = qty > 1 ? `×${qty}` : '✓';
      card.querySelector('[data-add]').hidden = qty > 0;
      card.querySelector('[data-step]').hidden = qty === 0;
    }

    const reached = this.tiers.filter((tier) => pieces >= tier.qty).pop();
    const next = this.tiers.find((tier) => pieces < tier.qty);
    const discounted = reached ? Math.round(total * (1 - reached.pct / 100)) : total;

    this.querySelector('[data-pieces]').textContent = `${pieces} ${pieces === 1 ? 'piece' : 'pieces'}`;
    this.querySelector('[data-total]').textContent = this.format(discounted);
    const was = this.querySelector('[data-was]');
    was.hidden = !reached;
    was.textContent = reached ? this.format(total) : '';

    [...this.querySelectorAll('[data-prog] i')].forEach((bar, index) => bar.classList.toggle('is-on', index < pieces));

    const msg = this.querySelector('[data-msg]');
    if (next) {
      const more = next.qty - pieces;
      msg.innerHTML = `Add ${more} more ${more === 1 ? 'piece' : 'pieces'} → unlock <b>${next.label}</b>`;
    } else if (reached) {
      msg.innerHTML = `<b>${reached.label}</b> unlocked — applied at checkout`;
    } else {
      msg.textContent = '';
    }

    const cta = this.querySelector('[data-cta]');
    cta.dataset.label ||= cta.textContent.trim();
    cta.disabled = pieces === 0;
    cta.textContent = pieces ? `${cta.dataset.label} · ${this.format(discounted)}` : cta.dataset.label;

    this.panel.classList.toggle('is-empty', pieces === 0);
    this.renderLines();
  }

  renderLines() {
    const thumbs = this.querySelector('[data-thumbs]');
    const list = this.querySelector('[data-list]');
    const empty = list.querySelector('.csb__listempty');
    thumbs.replaceChildren();
    list.querySelectorAll('[data-line]').forEach((line) => line.remove());
    empty.hidden = this.set.size > 0;

    for (const id of this.order) {
      const card = this.byId.get(id);
      const qty = this.set.get(id);
      const { title, img, price } = card.dataset;

      const thumb = document.createElement('div');
      thumb.className = 'csb__th';
      thumb.dataset.line = id;
      thumb.innerHTML = `<img src="${img}" alt=""><button type="button" class="csb__x" data-remove aria-label="Remove">×</button>${
        qty > 1 ? `<span class="csb__n">${qty}</span>` : ''
      }`;
      thumbs.append(thumb);

      const line = document.createElement('div');
      line.className = 'csb__li';
      line.dataset.line = id;
      line.innerHTML = `<img src="${img}" alt=""><div class="csb__lt"><b></b><span>${this.format(Number(price))}</span></div>
        <div class="csb__mini"><button type="button" data-minus aria-label="Remove one">−</button><span>${qty}</span><button type="button" data-plus aria-label="Add one more">+</button></div>`;
      line.querySelector('b').textContent = card.querySelector('.csb__nm').textContent;
      line.querySelector('img').alt = title;
      list.append(line);
    }
  }

  openModal(card) {
    this.modalId = card.dataset.id;
    const sources = card.dataset.gallery.split('|').filter(Boolean);
    const gallery = this.modal.querySelector('[data-gal]');
    const thumbs = this.modal.querySelector('[data-gthumbs]');
    gallery.replaceChildren(
      ...sources.map((src, index) => {
        const img = document.createElement('img');
        img.src = src;
        img.alt = card.dataset.title;
        img.loading = index === 0 ? 'eager' : 'lazy';
        return img;
      })
    );
    thumbs.replaceChildren(
      ...sources.map((src, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.gthumb = String(index);
        button.setAttribute('aria-label', `Image ${index + 1}`);
        const img = document.createElement('img');
        img.src = src.replace('width=900', 'width=140');
        img.alt = '';
        button.append(img);
        return button;
      })
    );
    const several = sources.length > 1;
    thumbs.hidden = !several;
    this.modal.querySelector('[data-gprev]').hidden = !several;
    this.modal.querySelector('[data-gnext]').hidden = !several;
    this.modal.querySelector('[data-gctr]').hidden = !several;
    gallery.onscroll = () => this.updateGallery();
    gallery.scrollLeft = 0;
    this.updateGallery(0);

    this.modal.querySelector('[data-mtitle]').textContent = card.dataset.short || card.dataset.title;
    this.modal.querySelector('[data-mprice]').innerHTML = card.querySelector('.csb__pr').innerHTML;
    this.modal.querySelector('[data-mchip]').hidden = card.dataset.chip !== 'true';
    const add = this.modal.querySelector('[data-madd]');
    add.dataset.label ||= add.textContent.trim();
    add.textContent = `${add.dataset.label} · ${this.format(Number(card.dataset.price))}`;
    this.lastFocus = document.activeElement;
    this.modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    this.modal.querySelector('[data-close]').focus();
  }

  galleryIndex() {
    const gallery = this.modal.querySelector('[data-gal]');
    return gallery.clientWidth ? Math.round(Math.abs(gallery.scrollLeft) / gallery.clientWidth) : 0;
  }

  updateGallery(index = this.galleryIndex()) {
    const thumbs = [...this.modal.querySelectorAll('[data-gthumb]')];
    const count = thumbs.length || 1;
    this.modal.querySelector('[data-gctr]').textContent = `${index + 1}/${count}`;
    thumbs.forEach((thumb, i) => thumb.classList.toggle('is-on', i === index));
  }

  goToImage(index) {
    const gallery = this.modal.querySelector('[data-gal]');
    const count = gallery.children.length;
    if (!count) return;
    const next = (index + count) % count;
    gallery.scrollTo({ left: next * gallery.clientWidth, behavior: 'smooth' });
    this.updateGallery(next);
  }

  closeModal() {
    this.modal.hidden = true;
    document.documentElement.style.overflow = '';
    this.modalId = null;
    if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
  }

  async addToCart() {
    const cta = this.querySelector('[data-cta]');
    const error = this.querySelector('[data-err]');
    error.hidden = true;
    cta.disabled = true;
    const items = this.order.map((id) => ({ id: Number(id), quantity: this.set.get(id) }));
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || 'Could not add your set');
      const count = items.reduce((sum, item) => sum + item.quantity, 0);
      this.set.clear();
      this.order = [];
      this.render();
      if (sections.length) {
        document.dispatchEvent(
          new CartAddEvent({}, 'cs-bundle', { source: 'cs-bundle', itemCount: count, sections: data.sections })
        );
      } else {
        window.location.href = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart` : '/cart';
      }
    } catch (err) {
      error.textContent = err.message;
      error.hidden = false;
      cta.disabled = false;
    }
  }
}

if (!customElements.get('cs-bundle')) customElements.define('cs-bundle', CsBundle);
